import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { SectionHead, Reveal } from "./Reveal";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

const spans = [
  "md:col-span-7 aspect-[4/5] md:aspect-auto md:row-span-2",
  "md:col-span-5 aspect-[4/3]",
  "md:col-span-5 aspect-[4/3]",
];

const Card = ({ item, i, view, onOpen }) => (
  <Reveal delay={(i % 2) * 0.1} className={`relative ${spans[i]}`}>
    <button
      data-testid={`work-card-${item.id}`}
      onClick={() => onOpen(item)}
      className="group relative block w-full h-full min-h-[280px] overflow-hidden text-left bg-ink-3"
    >
      <img src={item.img} alt={item.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover grayscale-[60%] group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-[transform,filter] [transition-duration:1200ms] [transition-timing-function:cubic-bezier(.7,0,.2,1)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(11,10,9,0.9),rgba(11,10,9,0.1)_55%)]" />
      <div className="absolute bottom-0 inset-x-0 p-6 md:p-8 flex items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-gold mb-3">{item.cat} — {item.city}</p>
          <h3 className="font-display text-3xl md:text-4xl">{item.title}</h3>
        </div>
        <span className="shrink-0 w-12 h-12 rounded-full border border-[var(--bone)]/40 flex items-center justify-center group-hover:bg-[var(--gold)] group-hover:text-[var(--ink)] group-hover:border-[var(--gold)] transition-colors duration-500" aria-label={view}>
          <ArrowUpRight size={18} />
        </span>
      </div>
    </button>
  </Reveal>
);

export const Work = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(null);
  const items = Array.isArray(t.work.items) ? t.work.items : [];

  return (
    <section id="work" data-testid="work-section" className="px-6 md:px-12 py-28 md:py-40 bg-ink-2">
      <SectionHead id="work" over={t.work.over} title={t.work.title} title2={t.work.title2} />
      <div className="mt-20 grid grid-cols-1 md:grid-cols-12 md:auto-rows-[minmax(300px,auto)] gap-4 md:gap-6">
        {items.map((it, i) => (
          <Card key={it.id} item={it} i={i} view={t.work.view} onOpen={setOpen} />
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl bg-[var(--ink)] border-line p-0 overflow-hidden" data-testid="work-dialog">
          {open && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <img src={open.img} alt={open.title} className="w-full aspect-[16/9] object-cover" />
              <div className="p-8">
                <DialogTitle className="font-display text-4xl font-normal">{open.title}</DialogTitle>
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-gold mt-3">{open.cat} — {open.city}</p>
                <DialogDescription className="text-stone text-base mt-6 leading-relaxed">{open.desc}</DialogDescription>
              </div>
            </motion.div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
